import {
  DataBrowserTab,
  DataConnectionId,
  DataSourceSchemaId,
  DataSourceTableId,
  EnumValues,
  HexId,
  SemanticDatasetId,
  SemanticViewId,
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- no side effects
  typedObjectValues,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  DataBrowserRecommendedViewPanel,
  DataBrowserRecommendedViewSelection,
} from "../../components/data/data-browser-recommended-view/utils/recommendedView.js";
import { CellLocation } from "../utils/cell-location/index.js";

export const DataBrowserSearchResultType = {
  DATAFRAME: "Dataframe",
  TABLE: "Table",
  SCHEMA: "Schema",
  DATABASE: "Database",
} as const;

export type DataBrowserSearchResultType = EnumValues<
  typeof DataBrowserSearchResultType
>;
export const DefaultSearchResultTypes = typedObjectValues(
  DataBrowserSearchResultType,
);

export type DataBrowserState = {
  magicAllowed: boolean;
  dataManagerDataConnectionId: DataConnectionId | null;
  hexId?: string;
  insertLocation: CellLocation;
  [DataBrowserTab.RECENTLY_USED]: {
    dataBrowserDataConnectionId: DataConnectionId | null;
    dataBrowserTableId: DataSourceTableId | null;
    dataBrowserSemanticDatasetId: SemanticDatasetId | null;
    displayObjectType: "table" | "semanticDataset";
  };
  [DataBrowserTab.CURATION]: {
    dataBrowserDataConnectionId: DataConnectionId | null;
    dataBrowserTableId: DataSourceTableId | null;
  };
  [DataBrowserTab.FAVORITES]: {
    dataBrowserDataConnectionId: DataConnectionId | null;
    dataBrowserTableId: DataSourceTableId | null;
    dataBrowserSchemaId: DataSourceSchemaId | null;
    dataBrowserSemanticDatasetId: SemanticDatasetId | null;
    dataBrowserSemanticViewId: SemanticViewId | null;
    displayObjectType: "table" | "schema" | "semanticDataset" | "semanticView";
  };
  [DataBrowserTab.RECOMMENDED]: {
    panel: DataBrowserRecommendedViewPanel;
    selection: DataBrowserRecommendedViewSelection;
  };
};

export const DATA_BROWSER_EMPTY_SELECTION = {
  dataBrowserDataConnectionId: null,
  dataBrowserTableId: null,
  dataBrowserSchemaId: null,
  dataBrowserSemanticDatasetId: null,
  dataBrowserSemanticViewId: null,
  displayObjectType: "semanticDataset" as const,
};

const initialDataBrowserState: DataBrowserState = {
  magicAllowed: false,
  dataManagerDataConnectionId: null,
  insertLocation: { type: "global" },
  [DataBrowserTab.RECENTLY_USED]: {
    ...DATA_BROWSER_EMPTY_SELECTION,
    displayObjectType: "table",
  },
  [DataBrowserTab.CURATION]: DATA_BROWSER_EMPTY_SELECTION,
  [DataBrowserTab.FAVORITES]: DATA_BROWSER_EMPTY_SELECTION,
  [DataBrowserTab.RECOMMENDED]: {
    panel: DataBrowserRecommendedViewPanel.OVERVIEW,
    selection: DATA_BROWSER_EMPTY_SELECTION,
  },
};

type SetOrgMagicAllowed = {
  magicAllowed: boolean;
};

type SetDataConnectionId = {
  dataConnectionId: DataConnectionId | null;
};

type SetTable = {
  tab: Exclude<DataBrowserTab, "DATA">;
  tableId: DataSourceTableId | null;
  dataConnectionId: DataConnectionId | null;
};

type SetSchema = {
  schemaId: DataSourceSchemaId | null;
};

type SetSemanticDataset = {
  tab: Exclude<DataBrowserTab, "DATA">;
  semanticDatasetId: SemanticDatasetId | null;
  dataConnectionId: DataConnectionId | null;
};

type SetSemanticView = {
  tab: Exclude<DataBrowserTab, "DATA">;
  semanticViewId: SemanticViewId | null;
  dataConnectionId: DataConnectionId | null;
};

type SetFavoritesDisplayObjectType = {
  displayObjectType: "table" | "schema" | "semanticDataset";
};

const dataBrowserSlice = createSlice({
  name: "dataBrowser",
  initialState: initialDataBrowserState,
  reducers: {
    setMagicAllowedForDataBrowser: (
      state,
      action: PayloadAction<SetOrgMagicAllowed>,
    ) => {
      state.magicAllowed = action.payload.magicAllowed;
    },
    setDataConnectionIdForDataManager: (
      state,
      action: PayloadAction<SetDataConnectionId>,
    ) => {
      state.dataManagerDataConnectionId = action.payload.dataConnectionId;
    },
    setTableForDataBrowser: (state, action: PayloadAction<SetTable>) => {
      const { dataConnectionId, tab, tableId } = action.payload;
      if (tab === DataBrowserTab.RECENTLY_USED) {
        state[DataBrowserTab.RECENTLY_USED].dataBrowserTableId = tableId;
        state[DataBrowserTab.RECENTLY_USED].dataBrowserDataConnectionId =
          dataConnectionId;
        state[DataBrowserTab.RECENTLY_USED].dataBrowserSemanticDatasetId = null;
        state[DataBrowserTab.RECENTLY_USED].displayObjectType = "table";
      } else if (tab === DataBrowserTab.CURATION) {
        state[DataBrowserTab.CURATION].dataBrowserTableId = tableId;
        state[DataBrowserTab.CURATION].dataBrowserDataConnectionId =
          dataConnectionId;
      } else {
        state[DataBrowserTab.FAVORITES].dataBrowserTableId = tableId;
        state[DataBrowserTab.FAVORITES].dataBrowserDataConnectionId =
          dataConnectionId;
        state[DataBrowserTab.FAVORITES].displayObjectType = "table";
      }
    },
    setSemanticDatasetForDataBrowser: (
      state,
      action: PayloadAction<SetSemanticDataset>,
    ) => {
      const { dataConnectionId, semanticDatasetId, tab } = action.payload;
      if (tab === DataBrowserTab.RECENTLY_USED) {
        state[DataBrowserTab.RECENTLY_USED].dataBrowserSemanticDatasetId =
          semanticDatasetId;
        state[DataBrowserTab.RECENTLY_USED].dataBrowserDataConnectionId =
          dataConnectionId;
        state[DataBrowserTab.RECENTLY_USED].dataBrowserTableId = null;
        state[DataBrowserTab.RECENTLY_USED].displayObjectType =
          "semanticDataset";
      } else {
        state[DataBrowserTab.FAVORITES].dataBrowserSemanticDatasetId =
          semanticDatasetId;
        state[DataBrowserTab.FAVORITES].dataBrowserDataConnectionId =
          dataConnectionId;
        state[DataBrowserTab.FAVORITES].displayObjectType = "semanticDataset";
      }
    },
    setSemanticViewForDataBrowser: (
      state,
      action: PayloadAction<SetSemanticView>,
    ) => {
      const { dataConnectionId, semanticViewId, tab } = action.payload;
      if (tab === DataBrowserTab.RECENTLY_USED) {
        // TODO: implement
      } else {
        state[DataBrowserTab.FAVORITES].dataBrowserSemanticViewId =
          semanticViewId;
        state[DataBrowserTab.FAVORITES].dataBrowserDataConnectionId =
          dataConnectionId;
        state[DataBrowserTab.FAVORITES].displayObjectType = "semanticView";
      }
    },
    setFavoritesDisplayObjectType: (
      state,
      action: PayloadAction<SetFavoritesDisplayObjectType>,
    ) => {
      state[DataBrowserTab.FAVORITES].displayObjectType =
        action.payload.displayObjectType;
    },
    // currently only applicable to favorites tab
    setFavoritesSelectedSchema: (state, action: PayloadAction<SetSchema>) => {
      state[DataBrowserTab.FAVORITES].dataBrowserSchemaId =
        action.payload.schemaId;
    },
    /**
     * If the current hexId is non-null and different than the provided hexId,
     * we will also reset the state
     */
    setDataBrowserHexId: (state, action: PayloadAction<HexId | null>) => {
      if (state.hexId === action.payload) {
        return;
      }
      // state can be project specific, so if the previous hexId corresponds to
      // a different project, we reset it.
      if (state.hexId != null) {
        state[DataBrowserTab.FAVORITES] =
          initialDataBrowserState[DataBrowserTab.FAVORITES];
        state[DataBrowserTab.CURATION] =
          initialDataBrowserState[DataBrowserTab.CURATION];
        state[DataBrowserTab.RECENTLY_USED] =
          initialDataBrowserState[DataBrowserTab.RECENTLY_USED];
        state[DataBrowserTab.RECOMMENDED] =
          initialDataBrowserState[DataBrowserTab.RECOMMENDED];
      }
      state.hexId = action.payload ?? undefined;
    },
    /** Modify the recommended view state by switching panels
     * and/or the current selection. */
    setDataBrowserRecommendedView: (
      state,
      action: PayloadAction<
        Partial<DataBrowserState[typeof DataBrowserTab.RECOMMENDED]>
      >,
    ) => {
      if (action.payload.panel !== undefined) {
        state[DataBrowserTab.RECOMMENDED].panel = action.payload.panel;
      }
      if (action.payload.selection !== undefined) {
        state[DataBrowserTab.RECOMMENDED].selection = action.payload.selection;
      }
    },
    setInsertLocation: (state, action: PayloadAction<CellLocation>) => {
      state.insertLocation = action.payload;
    },
  },
});

export const dataBrowserTreeReducer = dataBrowserSlice.reducer;

export const {
  setDataBrowserHexId,
  setDataBrowserRecommendedView,
  setDataConnectionIdForDataManager,
  setFavoritesDisplayObjectType,
  setFavoritesSelectedSchema: setSchemaForDataBrowser,
  setInsertLocation,
  setMagicAllowedForDataBrowser,
  setSemanticDatasetForDataBrowser,
  setSemanticViewForDataBrowser,
  setTableForDataBrowser,
} = dataBrowserSlice.actions;
